import _isNaN from 'lodash.isnan';
import _isFinite from 'lodash.isfinite';
import _toNumber from 'lodash.tonumber';
import { isValueEmpty } from './utilsReact';

function isNumeric(value) {
  return !_isNaN(parseFloat(value)) && _isFinite(value);
}

const sortingOrder = (a, b, order) => {
  if (a == b) return 0;

  let isSortingAsc = order.toLowerCase() === 'asc';
  if (isSortingAsc ? a > b : a < b) {
    return 1;
  } else {
    return -1;
  }
};

export function dataTablesSortCI(data, sort, column) {
  let field = column['field'] || this.field;

  return data.sort((a, b) => {
    /* eslint-disable no-prototype-builtins */
    if (!a.hasOwnProperty(field) || !b.hasOwnProperty(field)) return 0;

    let aVal = a[field] === null ? '' : a[field].toLowerCase();
    let bVal = b[field] === null ? '' : b[field].toLowerCase();
    return sortingOrder(aVal, bVal, sort);
  });
}

// Purpose : Sort callback for objects.	E.g WeatherStation as data with Name as column
export function dataTablesSortCIObject(data, sort, column) {
  let field = column['field'] || this.field;

  return data.sort((a, b) => {
    /* eslint-disable no-prototype-builtins */
    if (!a.hasOwnProperty(field) || !b.hasOwnProperty(field)) return 0;

    let aVal = a[field] === null || a[field][this.column] === null ? '' : a[field][this.column].toLowerCase();

    let bVal = b[field] === null || b[field][this.column] === null ? '' : b[field][this.column].toLowerCase();

    return sortingOrder(aVal, bVal, sort);
  });
}

export function dataTablesNumberSortCI(data, sort, column) {
  let field = column['field'] || this.field;

  return data.sort((a, b) => {
    if (!Object.prototype.hasOwnProperty.call(a, field) || !Object.prototype.hasOwnProperty.call(b, field)) return 0;

    let aVal = a[field];
    let bVal = b[field];

    return sortingOrder(aVal, bVal, sort);
  });
}
export function sortByNumericThenAlpha(rowA, rowB, id) {
  if (!rowA || !rowB) {
    // return default react-table sorting
    return 'basic';
  }
  if (!rowA.values[id] && !rowB.values[id]) {
    // return default react-table sorting
    return 'basic';
  }
  if (!rowB.values[id]) {
    return 1;
  }
  if (!rowA.values[id] && rowB.values[id]) {
    return -1; //b bigger
  }
  if (isValueEmpty(rowB.values[id])) {
    return 1;
  }
  if (isValueEmpty(rowA.values[id]) && !isValueEmpty(rowB.values[id])) {
    return -1;
  }
  if (isNumeric(rowA.values[id]) && isNumeric(rowB.values[id])) {
    return _toNumber(rowA.values[id]) >= _toNumber(rowB.values[id]) ? 1 : -1;
  }

  return (rowA.values[id] + '').toLowerCase().localeCompare((rowB.values[id] + '').toLowerCase());
}

export function sortMixedNumericAlpha(rowA, rowB, id) {
  const valA = rowA?.values?.[id];
  const valB = rowB?.values?.[id];

  if (!valA && !valB) return 0;
  if (!valA) return -1;
  if (!valB) return 1;

  if (isValueEmpty(valA) && isValueEmpty(valB)) return 0;
  if (isValueEmpty(valA)) return -1;
  if (isValueEmpty(valB)) return 1;

  const numA = parseFloat(valA);
  const numB = parseFloat(valB);

  const isNumA = !isNaN(numA);
  const isNumB = !isNaN(numB);

  if (isNumA && isNumB) {
    return numA - numB;
  }

  if (isNumA && !isNumB) return -1;
  if (!isNumA && isNumB) return 1;

  return valA.toString().toLowerCase().localeCompare(valB.toString().toLowerCase());
}

export function sortDataTablesByNumericThenAlpha(rowA, rowB, id) {
  if (!rowA || !rowB) {
    // return default react-table sorting
    return 'basic';
  }
  if (!rowA.values[id] && !rowB.values[id]) {
    // return default react-table sorting
    return 'basic';
  }
  if (!rowB.values[id]) {
    return 1;
  }
  if (!rowA.values[id] && rowB.values[id]) {
    return -1; //b bigger
  }
  if (isValueEmpty(rowB.values[id].Value)) {
    return 1;
  }
  if (isValueEmpty(rowA.values[id].Value) && !isValueEmpty(rowB.values[id].Value)) {
    return -1;
  }
  if (isNumeric(rowA.values[id].Value) && isNumeric(rowB.values[id].Value)) {
    return _toNumber(rowA.values[id].Value) >= _toNumber(rowB.values[id].Value) ? 1 : -1;
  }

  return (rowA.values[id].Value + '').toLowerCase().localeCompare((rowB.values[id].Value + '').toLowerCase());
}

/**
 * Sorts an array of objects using the merge sort algorithm based on the specified property.
 * This function ensures stable sorting by preserving the original order of elements with equal sort values.
 *
 * @param {Object[]} arr - The array of objects to be sorted.
 * @param {string} prop - The name of the property used for sorting.
 * @returns {Object[]} - A new sorted array containing the objects sorted by the specified property.
 */
export const mergeSort = (arr, prop) => {
  // If the array has one element or is empty, it is already sorted, so return it as is.
  if (arr.length <= 1) return arr;

  // Find the middle index to divide the array into left and right parts.
  const middle = Math.floor(arr.length / 2);

  // Slice the array into left and right parts.
  const left = arr.slice(0, middle);
  const right = arr.slice(middle);

  // Recursively apply mergeSort to left and right parts, then merge the results.
  return merge(mergeSort(left, prop), mergeSort(right, prop), prop);
};

/**
 * Merges two sorted arrays into a single sorted array based on the specified property.
 * This function ensures stable sorting by preserving the original order of elements with equal sort values.
 *
 * @param {Object[]} left - The left array to be merged.
 * @param {Object[]} right - The right array to be merged.
 * @param {string} prop - The name of the property used for sorting.
 * @returns {Object[]} - A new sorted array containing the objects merged and sorted by the specified property.
 */
const merge = (left, right, prop) => {
  let result = [];
  let leftIndex = 0;
  let rightIndex = 0;

  // Compare elements from the left and right arrays and push the smaller one into the result array.
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex][prop] < right[rightIndex][prop]) {
      result.push(left[leftIndex]);
      leftIndex++;
    } else {
      result.push(right[rightIndex]);
      rightIndex++;
    }
  }

  // Concatenate any remaining elements from left and right arrays.
  return result.concat(left.slice(leftIndex)).concat(right.slice(rightIndex));
};
